import type {RelayLease, RelayLeaseReleaseReason} from '@github-ui/ahp-relay/relay-registry'
import {acquireRelayLease} from '@github-ui/ahp-relay/relay-registry'
import {getInputRequests} from '@github-ui/ahp-relay/ahp-session-selectors'
import {AhpSessionClient} from './session-client'
import type {
  EstablishedAhpPlanningPolicy,
  EstablishedAhpPlan,
  EstablishedAhpPlanningState,
  EstablishedAhpSession,
  EstablishedAhpSessionLifecycle,
  EstablishedAhpSessionPlanning,
  EstablishedAhpSessionProvisioned,
  EstablishedAhpResumptionAttempt,
  EstablishedAhpLifecycleState,
} from './contracts'
import {EstablishedAhpSessionError as SessionError, EstablishedAhpSessionResumptionTimeoutError} from './contracts'
import {relayFailureFromError} from '@github-ui/ahp-relay/relay-failure'
import {createEstablishedAhpResumptionDeadline} from './resumption-deadline'
import type {RelayConnectionProgress} from '@github-ui/ahp-relay/relay-connection-runtime'
import {getDocumentPageActivity, type PageActivity} from '@github-ui/ahp-relay/page-activity'
import {ConnectionAdmissionSkippedError, type ConnectionAdmission} from '@github-ui/ahp-relay/connection-admission'

interface EstablishedAhpSessionRuntime {
  readonly session: EstablishedAhpSession
  start(): Promise<void>
  wakeIfReconnecting(admission?: ConnectionAdmission): Promise<void>
  activateResumptionRecovery(): void
  waitForAccepted(receipt: Parameters<AhpSessionClient['waitForAccepted']>[0]): Promise<void>
  reset(): void
  dispose(reason?: RelayLeaseReleaseReason): void
}

interface EstablishedAhpSessionRuntimeOptions extends EstablishedAhpSessionProvisioned {
  readonly planning?: EstablishedAhpPlanningPolicy
  readonly resumptionDeadline?: EstablishedAhpResumptionAttempt
  readonly pageActivity?: PageActivity
  readonly awaitIntentAfterLive?: boolean
  readonly connectionAdmission?: ConnectionAdmission
}

function lifecycleStateFor(status: ReturnType<RelayLease['getStatus']>): EstablishedAhpLifecycleState {
  switch (status) {
    case 'connected':
      return 'live'
    case 'reconnecting':
      return 'reconnecting'
    case 'error':
    case 'closed':
      return 'failed'
    default:
      return 'connecting'
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'The established AHP session failed.'
}

/**
 * Internal factory used by bootstrap coordinators after they have resolved a
 * full session identity and relay adapter. It keeps the relay lease and client
 * private to the runtime returned to the bootstrap.
 */
export async function createEstablishedAhpSessionRuntime(
  options: EstablishedAhpSessionRuntimeOptions,
): Promise<EstablishedAhpSessionRuntime> {
  if (options.planning?.enabled && !options.planning.adapter) {
    throw new SessionError(
      'establish',
      'connecting',
      'Active-AHP planning is enabled but no planning adapter is configured.',
      false,
    )
  }

  const relay = {
    ...options.relay,
    awaitIntentAfterLive: options.awaitIntentAfterLive === true,
    environmentId: options.identity.environmentId,
    sessionId: options.identity.sessionId,
    ...(options.identity.channel ? {channel: options.identity.channel} : {}),
    ...(options.identity.taskId ? {taskId: options.identity.taskId} : {}),
  }
  let reconnectDeadline: EstablishedAhpResumptionAttempt | undefined
  const activeResumptionDeadline = () =>
    options.resumptionDeadline && !options.resumptionDeadline.isDisposed
      ? options.resumptionDeadline
      : reconnectDeadline
  const advanceResumptionPhase = (progress: RelayConnectionProgress | undefined) => {
    const attempt = activeResumptionDeadline()
    if (!attempt || attempt.isDisposed || !progress) return
    try {
      if (progress.phase === 'waking-environment') {
        attempt.advanceTo('waking')
      } else if (progress.phase === 'provisioning-repository') {
        attempt.advanceTo('provisioning')
      } else if (progress.phase === 'starting-agent') {
        attempt.advanceTo('starting-agent')
      } else if (
        progress.phase === 'opening-transport' ||
        progress.phase === 'initializing-connection' ||
        progress.phase === 'authenticating' ||
        progress.phase === 'restoring-session' ||
        progress.phase === 'restoring-conversation' ||
        progress.phase === 'connected'
      ) {
        attempt.advanceTo('connecting')
      }
    } catch (error) {
      if (!attempt.signal.aborted) throw error
    }
  }
  const lease = await acquireRelayLease(relay, {
    signal: options.resumptionDeadline?.signal,
    onConnectionProgress: options.resumptionDeadline ? advanceResumptionPhase : undefined,
    admission: options.connectionAdmission,
  })
  const pageActivity = options.pageActivity ?? getDocumentPageActivity()
  const reconnectActivity: PageActivity = {
    isActive: () =>
      pageActivity.isActive() && lease.isCurrent() && lease.getConnectionProgress?.()?.phase !== 'awaiting-intent',
    subscribe: listener => {
      const unsubscribePage = pageActivity.subscribe(listener)
      const unsubscribeLease = lease.subscribe(listener)
      return () => {
        unsubscribePage()
        unsubscribeLease()
      }
    },
  }
  advanceResumptionPhase(lease.getConnectionProgress?.())
  const unsubscribeResumptionProgress = lease.subscribe(() => {
    if (!lease.isCurrent()) return
    advanceResumptionPhase(lease.getConnectionProgress?.())
  })
  const lifecycleListeners = new Set<() => void>()
  let lifecycleState: EstablishedAhpLifecycleState = lifecycleStateFor(lease.getStatus())
  let disposed = false
  let relayReleased = false
  let resumptionRecoveryActive = false
  let hasBeenLive = false
  let planningState: EstablishedAhpPlanningState = {status: 'idle'}
  let unsubscribeRelay = () => {}

  const setLifecycleState = (next: EstablishedAhpLifecycleState) => {
    if (next === lifecycleState) return
    lifecycleState = next
    for (const listener of lifecycleListeners) listener()
  }

  const releaseRelay = (cancelConnection: boolean, reason: RelayLeaseReleaseReason) => {
    if (relayReleased) return
    relayReleased = true
    unsubscribeResumptionProgress()
    if (cancelConnection) lease.cancelConnection(reason)
    else lease.release(reason)
  }

  const failResumption = (error: SessionError) => {
    if (disposed || lifecycleState === 'failed') return
    reconnectDeadline?.dispose()
    reconnectDeadline = undefined
    client.markResumptionTimedOut(error)
    setLifecycleState('failed')
    unsubscribeRelay()
    releaseRelay(true, 'resumption-timeout')
  }

  const startReconnectDeadline = () => {
    if (!resumptionRecoveryActive || !hasBeenLive || reconnectDeadline || disposed) return
    reconnectDeadline = options.awaitIntentAfterLive
      ? createEstablishedAhpResumptionDeadline(undefined, {activity: reconnectActivity})
      : createEstablishedAhpResumptionDeadline()
    reconnectDeadline.signal.addEventListener(
      'abort',
      () => {
        const reason = reconnectDeadline?.signal.reason
        if (reason instanceof SessionError) failResumption(reason)
      },
      {once: true},
    )
  }

  const client = new AhpSessionClient(
    options.identity,
    lease,
    options.directTurnTelemetry,
    failResumption,
    () => resumptionRecoveryActive,
    start => {
      if (start) startReconnectDeadline()
      return reconnectDeadline
    },
  )

  unsubscribeRelay = lease.subscribe(() => {
    if (disposed || !lease.isCurrent()) return
    const next = lifecycleStateFor(lease.getStatus())
    if (next === 'live') {
      if (resumptionRecoveryActive) hasBeenLive = true
      reconnectDeadline?.dispose()
      reconnectDeadline = undefined
    } else if (next === 'connecting' || next === 'reconnecting') {
      startReconnectDeadline()
    }
    setLifecycleState(next)
  })

  const lifecycle: EstablishedAhpSessionLifecycle = {
    getState: () => lifecycleState,
    subscribe: listener => {
      lifecycleListeners.add(listener)
      return () => lifecycleListeners.delete(listener)
    },
  }

  const planningAdapter = options.planning?.enabled ? options.planning.adapter : undefined
  const refreshPlanningState = () => {
    if (!planningAdapter) return
    try {
      const state = client.view.getSnapshot().state
      let plan: EstablishedAhpPlan | undefined
      if (state) {
        for (const request of getInputRequests(state)) {
          plan = planningAdapter.identify(request)
          if (plan) break
        }
      }
      if (plan && plan.id !== planningState.plan?.id) {
        planningState = {status: 'pending', plan}
      } else if (!plan && planningState.status === 'pending') {
        planningState = {status: 'idle'}
      }
    } catch (error) {
      planningState = {status: 'failed', error: errorMessage(error)}
    }
  }
  const unsubscribePlanning = planningAdapter ? client.view.subscribe(refreshPlanningState) : () => {}
  refreshPlanningState()
  const planning: EstablishedAhpSessionPlanning | undefined = options.planning?.enabled
    ? {
        get state() {
          return planningState
        },
        actions: {
          approve: async () => {
            const plan = planningState.plan
            if (!plan || !planningAdapter) throw new Error('no active AHP plan is available to approve')
            try {
              await planningAdapter.approve(plan)
              planningState = {status: 'approved', plan}
            } catch (error) {
              planningState = {status: 'failed', plan, error: errorMessage(error)}
              throw error
            }
          },
          reject: async () => {
            const plan = planningState.plan
            if (!plan || !planningAdapter) throw new Error('no active AHP plan is available to reject')
            try {
              await planningAdapter.reject(plan)
              planningState = {status: 'rejected', plan}
            } catch (error) {
              planningState = {status: 'failed', plan, error: errorMessage(error)}
              throw error
            }
          },
          revise: async prompt => {
            const plan = planningState.plan
            if (!plan || !planningAdapter) throw new Error('no active AHP plan is available to revise')
            try {
              await planningAdapter.revise(plan, prompt)
              planningState = {status: 'pending', plan}
            } catch (error) {
              planningState = {status: 'failed', plan, error: errorMessage(error)}
              throw error
            }
          },
          continue: async () => {
            const plan = planningState.plan
            if (!plan || !planningAdapter) throw new Error('no active AHP plan is available to continue')
            try {
              await planningAdapter.continue(plan)
              planningState = {status: 'approved', plan}
            } catch (error) {
              planningState = {status: 'failed', plan, error: errorMessage(error)}
              throw error
            }
          },
        },
      }
    : undefined

  const session: EstablishedAhpSession = {
    identity: options.identity,
    view: client.view,
    commands: client.commands,
    input: client.input,
    ...(options.directTurnTelemetry ? {directTurnTelemetry: options.directTurnTelemetry} : {}),
    ...(planning ? {planning} : {}),
    lifecycle,
  }

  const runtime: EstablishedAhpSessionRuntime = {
    session,
    wakeIfReconnecting: async admission => {
      if (options.awaitIntentAfterLive && lease.getStatus() === 'reconnecting') {
        const started = await lease.ensureLive(undefined, admission)
        if (admission && !started && lease.getConnectionProgress?.()?.phase === 'awaiting-intent') {
          throw new ConnectionAdmissionSkippedError()
        }
      }
    },
    start: async () => {
      if (disposed) throw new Error('established AHP session runtime is disposed')
      try {
        const wake = runtime.wakeIfReconnecting(options.connectionAdmission)
        const deadline = options.resumptionDeadline
        if (deadline) {
          const connection = (async () => lease.whenConnected(undefined, undefined, deadline.signal))()
          const work = Promise.all([wake, connection])
          void (async () => {
            try {
              await work
            } catch {
              return
            }
          })()
          await deadline.waitFor(work)
        } else {
          await wake
          await lease.whenConnected()
        }
        setLifecycleState(lifecycleStateFor(lease.getStatus()))
      } catch (error) {
        if (
          error instanceof EstablishedAhpSessionResumptionTimeoutError ||
          (error instanceof SessionError && error.failure?.kind === 'resumption-timeout')
        ) {
          failResumption(error)
          throw error
        }
        setLifecycleState('failed')
        throw new SessionError('start', lifecycleState, errorMessage(error), true, {
          cause: error,
          failure: relayFailureFromError(error),
        })
      }
    },
    activateResumptionRecovery: () => {
      if (disposed || resumptionRecoveryActive) return
      resumptionRecoveryActive = true
      hasBeenLive = true
      if (lifecycleState === 'connecting' || lifecycleState === 'reconnecting') {
        startReconnectDeadline()
      }
    },
    waitForAccepted: receipt => client.waitForAccepted(receipt),
    reset: () => {
      runtime.dispose('runtime-reset')
    },
    dispose: (reason = 'runtime-disposed') => {
      if (disposed) return
      disposed = true
      reconnectDeadline?.dispose()
      reconnectDeadline = undefined
      setLifecycleState('disposed')
      unsubscribeRelay()
      unsubscribePlanning()
      client.markDisposed()
      releaseRelay(false, reason)
    },
  }

  return runtime
}
